import * as PropTypes from 'prop-types';
import React from 'react';

import AbsoluteDropdownContainer from 'ui-library/AbsoluteDropdownContainer';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';
import ModalAnchor from 'ui-library/ModalAnchor';
import ConfigureStageModal from 'workflows/configuration/ConfigureStageModal';
import UpdateStageRunStatusModal from 'workflows/modals/UpdateStageRunStatusModal';
import {
  STAGE_CONFIGURATION_FULL_SHAPE,
  StageConfiguration,
  WORKFLOW_STAGE_FULL_SHAPE,
  WorkflowStage,
} from 'workflows/WorkflowConstants';

interface StageMenuProps {
  baseWorkflowTemplateVersionId: string | null;
  className?: string;
  onBulkCreateStageRuns: (stageConfiguration: StageConfiguration) => void;
  onBulkSplitStageRuns: (workflowStage: WorkflowStage) => void;
  onCreate: (workflowStage: WorkflowStage) => void;
  stageConfiguration: StageConfiguration;
  workflowStage: WorkflowStage;
}

interface State {
  isDropdownOpen: boolean;
  isUpdateStageRunStatusModalOpen: boolean;
  isConfigureStageModalOpen: boolean;
  editedStageConfiguration: StageConfiguration | null;
  index: number | null;
}

class StageMenu extends React.Component<StageMenuProps, State> {
  static propTypes = {
    baseWorkflowTemplateVersionId: PropTypes.string,
    className: PropTypes.string,
    onBulkCreateStageRuns: PropTypes.func.isRequired,
    onBulkSplitStageRuns: PropTypes.func.isRequired,
    onCreate: PropTypes.func.isRequired,
    stageConfiguration: STAGE_CONFIGURATION_FULL_SHAPE.isRequired,
    workflowStage: WORKFLOW_STAGE_FULL_SHAPE.isRequired,
  };

  override state: State = {
    isDropdownOpen: false,
    isUpdateStageRunStatusModalOpen: false,
    isConfigureStageModalOpen: false,
    editedStageConfiguration: null,
    index: null,
  };

  _handleOpenDropdown = (): void => {
    this.setState({isDropdownOpen: true});
  };

  _handleCloseDropdown = (): void => {
    this.setState({isDropdownOpen: false});
  };

  _handleOpenUpdateStageRunStatusModel = (): void => {
    this.setState({isUpdateStageRunStatusModalOpen: true});
  };

  _handleCloseUpdateStageRunStatusModel = (): void => {
    this.setState({isUpdateStageRunStatusModalOpen: false});
  };

  _handleEditStage = (): void => {
    this.setState({
      isConfigureStageModalOpen: true,
      editedStageConfiguration: this.props.stageConfiguration,
    });
  };

  _handleAddStage = (): void => {
    this.setState({
      isConfigureStageModalOpen: true,
      editedStageConfiguration: null,
      index: this.props.stageConfiguration.index + 1,
    });
  };

  _handleCloseConfigureStageModal = (): void => {
    this.setState({isConfigureStageModalOpen: false});
  };

  override render(): JSX.Element {
    const {
      onBulkCreateStageRuns,
      onBulkSplitStageRuns,
      onCreate,
      workflowStage,
      stageConfiguration,
      className,
    } = this.props;
    return (
      <span className={className}>
        <AbsoluteDropdownContainer
          menu={
            <Menu height={MenuHeight.MEDIUM} width={MenuWidth.MEDIUM}>
              <Section>
                <Option
                  onSelect={() => {
                    this._handleOpenUpdateStageRunStatusModel();
                  }}
                >
                  Update status
                </Option>
              </Section>
              <Section>
                <Option
                  onSelect={() => {
                    onCreate(workflowStage);
                  }}
                >
                  Add run
                </Option>
                <Option
                  onSelect={() => {
                    onBulkCreateStageRuns(stageConfiguration);
                  }}
                >
                  Add runs inline
                </Option>
                <Option
                  onSelect={() => {
                    onBulkSplitStageRuns(workflowStage);
                  }}
                >
                  Split by inputs
                </Option>
              </Section>
              <Section>
                <Option
                  onSelect={() => {
                    this._handleEditStage();
                  }}
                >
                  Edit stage
                </Option>
                <Option
                  onSelect={() => {
                    this._handleAddStage();
                  }}
                >
                  Insert stage after
                </Option>
              </Section>
            </Menu>
          }
          onRequestClose={this._handleCloseDropdown}
          open={this.state.isDropdownOpen}
          shouldCloseOnInsideClick={true}
        >
          <BenchlingIcon
            className="scrollableTable-headerCell-rightIcon scrollableTable-headerCell-compose"
            icon="chevron-down"
            onClick={this._handleOpenDropdown}
          />
        </AbsoluteDropdownContainer>
        <ModalAnchor open={this.state.isUpdateStageRunStatusModalOpen}>
          {this.state.isUpdateStageRunStatusModalOpen && (
            <UpdateStageRunStatusModal
              onRequestClose={this._handleCloseUpdateStageRunStatusModel}
              workflowStageId={this.props.workflowStage.id}
            />
          )}
        </ModalAnchor>
        <ModalAnchor open={this.state.isConfigureStageModalOpen}>
          {this.state.isConfigureStageModalOpen && (
            <ConfigureStageModal
              baseWorkflowTemplateVersionId={this.props.baseWorkflowTemplateVersionId}
              stageConfiguration={this.state.editedStageConfiguration}
              onRequestClose={this._handleCloseConfigureStageModal}
              workflowTemplateVersionId={this.props.stageConfiguration.workflowTemplateVersion.id}
              index={this.state.index}
            />
          )}
        </ModalAnchor>
      </span>
    );
  }
}

export default StageMenu;
